import React, { Suspense, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useInViewAnimation from "../hooks/useInViewAnimation";
import ThemeToggle from "../components/ThemeToggle";
import "../styles/theme.css";
import "./UnifiedLanding.css";

// Lazy load large subcomponent (keep path/casing consistent)
const Testimonials = React.lazy(() => import("../components/Testimonials.jsx"));

/* Inline SVG Icons */
const IconGrowth = () => (
  <svg width="42" height="42" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" />
    <path d="M7 15l4-4 4 4 4-8" />
  </svg>
);
const IconFlexible = () => (
  <svg width="42" height="42" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1v22" />
    <path d="M7 5h10l3 4-3 4H7l-3-4 3-4z" />
    <path d="M7 15h10l3 4-3 4H7l-3-4 3-4z" />
  </svg>
);
const IconInstant = () => (
  <svg width="42" height="42" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
    <path d="M2 12h4" />
  </svg>
);
const IconExclusive = () => (
  <svg width="42" height="42" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10l4.5-2L12 11l4.5-3L21 10l-9 11-9-11z" />
  </svg>
);

const features = [
  {
    title: "Effortless Growth",
    detail:
      "Expand your financial possibilities with a platform designed for modern achievers. The bigger your network, the greater your rewards.",
    icon: <IconGrowth />,
  },
  {
    title: "Flexible Participation",
    detail:
      "Start instantly with your preferred payment method—cryptocurrency, credit card, or PayPal. No minimums. No boundaries. Jangcoin fits your lifestyle.",
    icon: <IconFlexible />,
  },
  {
    title: "Instant, Transparent Rewards",
    detail:
      "Enjoy immediate, secure rewards delivered to your wallet. All transactions are transparent and reliable, powered by blockchain.",
    icon: <IconInstant />,
  },
  {
    title: "Exclusive Member Advantages",
    detail:
      "Unlock invitations to private events, early access to new features, and premium opportunities reserved for our active community.",
    icon: <IconExclusive />,
  },
];

export default function UnifiedLanding() {
  const navigate = useNavigate();
  const [videoError, setVideoError] = useState(false);

  // Animate feature cards (safe hook – no-ops if IO unsupported)
  useInViewAnimation(".feature-card");

  // Theme-aware dynamic background
  const lightImages = ["/images/bg-light-1.jpg", "/images/bg-light-2.jpg", "/images/bg-light-3.jpg"];
  const darkImages  = ["/images/bg-dark-1.jpg",  "/images/bg-dark-2.jpg",  "/images/bg-dark-3.jpg"];

  const [isDark, setIsDark]   = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  const intervalRef = useRef(null);

  // Detect theme (supports data-theme or theme-dark class)
  useEffect(() => {
    const detect = () => {
      const rootAttr = document.documentElement.getAttribute("data-theme");
      const dark = (rootAttr && rootAttr.toLowerCase() === "dark") || document.body.classList.contains("theme-dark");
      setIsDark(dark);
    };
    detect();
    const mo = new MutationObserver(detect);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "class"] });
    mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  // Start rotation when theme changes. This effect does not reference bgIndex,
  // so eslint exhaustive-deps is satisfied and we don't need to disable it.
  useEffect(() => {
    const images = isDark ? darkImages : lightImages;
    // Ensure index stays within bounds if image set length changed
    setBgIndex((i) => i % images.length);

    // Clear any previous interval
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setBgIndex((i) => (i + 1) % images.length);
    }, 9000);

    return () => clearInterval(intervalRef.current);
  }, [isDark]); // depends only on theme

  // Preload the next image whenever index or theme changes (separate effect)
  useEffect(() => {
    const images = isDark ? darkImages : lightImages;
    const nextSrc = images[(bgIndex + 1) % images.length];
    const pre = new Image();
    pre.src = nextSrc;
  }, [bgIndex, isDark]);

  const images = isDark ? darkImages : lightImages;
  const current = images[bgIndex];
  const next    = images[(bgIndex + 1) % images.length];

  return (
    <main className="landing-main" aria-labelledby="hero-title">
      {/* Rotating background layers */}
      <div className="landing-bg" aria-hidden="true">
        <div className="landing-bg__layer landing-bg__layer--current" style={{ backgroundImage: `url(${current})` }} />
        <div className="landing-bg__layer landing-bg__layer--next"    style={{ backgroundImage: `url(${next})` }} />
      </div>

      <ThemeToggle />

      {/* LOGO */}
      <div className="logo-container" style={{ textAlign: "center", margin: "32px 0 16px" }}>
        <img src="/logo.png" alt="Jangcoin logo" style={{ width: 120, height: "auto" }} loading="lazy" />
      </div>

      {/* HERO */}
      <section className="hero-section" aria-labelledby="hero-title">
        <h1 id="hero-title" className="landing-title">
          Elevate Your Financial Journey With Jangcoin
        </h1>
        <p className="landing-subtitle">
          Unlock digital rewards, exclusive opportunities, and connect with a global network—all in real time.
        </p>

        <div className="video-container">
          <div className={`video-responsive ${videoError ? "video-error" : ""}`} aria-label="Intro video">
            {!videoError && (
              <iframe
                title="Jangcoin Introduction Video"
                src="https://www.youtube.com/embed/gw8btSs65MA?rel=0&autoplay=1&mute=1"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                onError={() => setVideoError(true)}
                style={{ border: 0 }}
              />
            )}
            {videoError && (
              <img className="video-poster" src="/images/video-poster.jpg" alt="Platform preview" loading="lazy" />
            )}
          </div>
        </div>

        <button
          type="button"
          className="cta-main-btn"
          onClick={() => navigate("/login?sponsor=admin")}
          aria-label="Start your journey and join Jangcoin"
        >
          Get Started
        </button>
      </section>

      {/* WELCOME */}
      <section aria-labelledby="welcome-title" className="welcome-section">
        <h2 id="welcome-title" className="section-title">Welcome to Jangcoin</h2>
        <p className="section-subtitle">Decentralized wealth creation powered by Bitcoin.</p>
      </section>

      {/* FEATURES */}
      <section aria-labelledby="features-title" className="features-section">
        <h2 id="features-title" className="section-title">Why Join Jangcoin?</h2>
        <div
          className="features-list"
          style={{ display: "flex", flexWrap: "wrap", gap: "18px", justifyContent: "center", marginTop: "12px" }}
        >
          {features.map((feat) => (
            <article key={feat.title} className="feature-card" tabIndex={0} aria-label={feat.title}>
              <div className="feature-icon-wrapper">{feat.icon}</div>
              <h3 className="feature-title">{feat.title}</h3>
              <p className="feature-detail">{feat.detail}</p>
            </article>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS (Lazy Loaded) */}
      <Suspense
        fallback={
          <div style={{ textAlign: "center", marginTop: "3rem" }}>
            <span style={{ fontSize: "0.95rem", color: "var(--color-text-subtle)" }} aria-live="polite">
              Loading community insights…
            </span>
          </div>
        }
      >
        <Testimonials />
      </Suspense>
    </main>
  );
}